import React from 'react';
import { useHistory } from 'react-router-dom';
import { ClipboardCheck, FileQuestion, PauseCircle, CalendarDays, RefreshCw } from 'lucide-react';
import PendingOrdersWidget from '../shared/PendingOrdersWidget';
import SearchAnalyticsWidget from '../shared/SearchAnalyticsWidget';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import {
  useOrderOverview,
  StatCardGrid,
  RecentOrdersCard,
  OrdersChartCard,
  LiveIndicator,
} from '../shared/OrderOverview';

// Staff see the same order overview as admins, linked to staff-area pages only
// (staff-only users can't open /admin routes).
const StaffDashboard = () => {
  const history = useHistory();
  const { stats, recentOrders, statusCounts, timeline, loading, realtimeConnected, refresh } =
    useOrderOverview('staff-dashboard');

  const statCards = [
    {
      label: 'Awaiting Approval',
      value: stats.pendingApprovals,
      sub: stats.pendingApprovals > 0 ? 'Review and send to Progress' : 'Nothing waiting',
      icon: ClipboardCheck,
      path: '/staff-area/approvals',
      needsAction: stats.pendingApprovals > 0,
    },
    {
      label: 'RFQs to Quote',
      value: stats.rfqsToQuote,
      sub: stats.rfqsToQuote > 0 ? 'Submitted or under review' : 'Nothing waiting',
      icon: FileQuestion,
      path: '/staff-area/inquiries',
      needsAction: stats.rfqsToQuote > 0,
    },
    {
      label: 'On Hold',
      value: stats.onHold,
      sub: 'Orders held in Progress',
      icon: PauseCircle,
      path: '/staff-area/orders',
    },
    {
      label: 'Orders This Week',
      value: stats.thisWeekOrders,
      sub: `${stats.lastWeekOrders} last week`,
      icon: CalendarDays,
      path: '/staff-area/orders',
      trend: stats.thisWeekOrders - stats.lastWeekOrders,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Orders and quotes waiting on the team today."
        actions={
          <>
            <LiveIndicator connected={realtimeConnected} />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refresh()}
              disabled={loading}
              icon={<RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />}
            >
              Refresh
            </Button>
          </>
        }
      />

      <StatCardGrid cards={statCards} loading={loading} />

      {/* Approval queue — only when there's something in it */}
      {stats.pendingApprovals > 0 && (
        <PendingOrdersWidget maxOrders={5} linkPath="/staff-area/approvals" />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersCard
          orders={recentOrders}
          loading={loading}
          viewAllPath="/staff-area/orders"
          orderPath={(order) => `/staff-area/orders?search=${encodeURIComponent(order.order_number)}`}
        />
        <OrdersChartCard statusCounts={statusCounts} timeline={timeline} loading={loading} />
      </div>

      <SearchAnalyticsWidget onViewFullAnalytics={() => history.push('/staff-area/search-analytics')} />
    </div>
  );
};

export default StaffDashboard;
